import React, { useState, useEffect, useRef } from "react";
import { FaMinus } from "react-icons/fa";

export default function DraggableTearsheetWrapper({
                                                      id,
                                                      title,
                                                      children,
                                                      minimized,
                                                      style = {},
                                                      onMinimize,
                                                      onClose,
                                                      zIndex,
                                                      onClick,
                                                      initialPos = { x: 200, y: 100 },
                                                      initialSize = { width: 600, height: 450 },
                                                      showMinimize = true,
                                                      showResize = true,
                                                  }) {
    const [pos, setPos] = useState(initialPos);
    const [size, setSize] = useState(initialSize);
    const [dragging, setDragging] = useState(false);
    const [resizing, setResizing] = useState(false);
    const [bodyDragArmed, setBodyDragArmed] = useState(false);
    const [rel, setRel] = useState({ x: 0, y: 0 });
    const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
    const pendingBodyDrag = useRef(null);

    const startDragAt = (pageX, pageY, e) => {
        // ignore if user started on a button, link, or interactive element
        if (e?.target?.closest?.("button, a, input, textarea, select, [role='button'], [contenteditable='true'], [data-no-drag]")) return;
        
        // ignore if user is selecting text
        if (window.getSelection && window.getSelection().toString().length > 0) return;

        setDragging(true);
        setRel({ x: pageX - pos.x, y: pageY - pos.y });
        onClick?.(id);
        e?.stopPropagation?.();
        e?.preventDefault?.();
    };

    const handleBannerMouseDown = (e) => {
        if (e.button !== 0) return;
        startDragAt(e.pageX, e.pageY, e);
    };

    const handleBannerTouchStart = (e) => {
        const t = e.touches?.[0];
        if (!t) return;
        startDragAt(t.pageX, t.pageY, e);
    };

    const handleBottomMouseDown = (e) => {
        if (e.button !== 0) return;
        startDragAt(e.pageX, e.pageY, e);
    };

    const handleBottomTouchStart = (e) => {
        const t = e.touches?.[0];
        if (!t) return;
        startDragAt(t.pageX, t.pageY, e);
    };

    const handleBodyMouseDown = (e) => {
        if (e.button !== 0) return;
        if (e?.target?.closest?.("button, a, input, textarea, select, [role='button'], [contenteditable='true'], [data-no-drag]")) return;
        pendingBodyDrag.current = { startX: e.pageX, startY: e.pageY };
        setBodyDragArmed(true);
    };

    const handleBodyTouchStart = (e) => {
        const t = e.touches?.[0];
        if (!t) return;
        if (e?.target?.closest?.("button, a, input, textarea, select, [role='button'], [contenteditable='true'], [data-no-drag]")) return;
        startDragAt(t.pageX, t.pageY, e);
    };

    const onResizeMouseDown = (e) => {
        if (e.button !== 0) return;
        setResizing(true);
        setResizeStart({ x: e.pageX, y: e.pageY, width: size.width, height: size.height });
        e.stopPropagation();
        e.preventDefault();
    };

    const onResizeTouchStart = (e) => {
        const t = e.touches?.[0];
        if (!t) return;
        setResizing(true);
        setResizeStart({ x: t.pageX, y: t.pageY, width: size.width, height: size.height });
        e.stopPropagation();
        e.preventDefault();
    };

    useEffect(() => {
        const onMouseMove = (e) => {
            if (!dragging && !resizing && pendingBodyDrag.current) {
                const dx = Math.abs(e.pageX - pendingBodyDrag.current.startX);
                const dy = Math.abs(e.pageY - pendingBodyDrag.current.startY);
                if (dx >= 6 || dy >= 6) {
                    // If selection is in progress, keep selection behavior and do not drag.
                    if (window.getSelection && window.getSelection().toString().length > 0) {
                        pendingBodyDrag.current = null;
                    } else {
                        setDragging(true);
                        setRel({ x: e.pageX - pos.x, y: e.pageY - pos.y });
                        onClick?.(id);
                        e?.stopPropagation?.();
                        e?.preventDefault?.();
                        pendingBodyDrag.current = null;
                    }
                }
            }

            if (dragging) {
                setPos({ x: e.pageX - rel.x, y: e.pageY - rel.y });
            } else if (resizing) {
                setSize({
                    width: Math.max(320, resizeStart.width + (e.pageX - resizeStart.x)),
                    height: Math.max(200, resizeStart.height + (e.pageY - resizeStart.y)),
                });
            }
        };

        const onMouseUp = () => {
            setDragging(false);
            setResizing(false);
            setBodyDragArmed(false);
            pendingBodyDrag.current = null;
        };

        const onTouchMove = (e) => {
            const t = e.touches?.[0];
            if (!t) return;

            if (dragging) {
                setPos({ x: t.pageX - rel.x, y: t.pageY - rel.y });
            } else if (resizing) {
                setSize({
                    width: Math.max(320, resizeStart.width + (t.pageX - resizeStart.x)),
                    height: Math.max(200, resizeStart.height + (t.pageY - resizeStart.y)),
                });
            }

            e.preventDefault();
        };

        const onTouchEnd = () => {
            setDragging(false);
            setResizing(false);
            setBodyDragArmed(false);
            pendingBodyDrag.current = null;
        };

        if (dragging || resizing || bodyDragArmed) {
            document.addEventListener("mousemove", onMouseMove);
            document.addEventListener("mouseup", onMouseUp);
            document.addEventListener("touchmove", onTouchMove, { passive: false });
            document.addEventListener("touchend", onTouchEnd);
        }

        return () => {
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup", onMouseUp);
            document.removeEventListener("touchmove", onTouchMove);
            document.removeEventListener("touchend", onTouchEnd);
        };
    }, [dragging, resizing, bodyDragArmed, rel, resizeStart, pos.x, pos.y, onClick, id]);

    return (
        <div
            className="fixed bg-white rounded-2xl shadow-2xl border border-gray-200 flex flex-col"
            style={{
                ...style,
                left: pos.x,
                top: pos.y,
                width: size.width,
                height: size.height,
                minWidth: 320,
                minHeight: 200,
                maxWidth: "98vw",
                maxHeight: "94vh",
                zIndex,
                display: showMinimize && minimized ? "none" : undefined,
                cursor: resizing ? "nwse-resize" : dragging ? "grabbing" : "default",
            }}
            tabIndex={-1}
        >
            {/* TOP BAR (drag) */}
            <div
                className="flex items-center justify-between px-4 py-2 bg-blue-900 text-white rounded-t-2xl cursor-grab select-none"
                onMouseDown={handleBannerMouseDown}
                onTouchStart={handleBannerTouchStart}
            >
                <span className="font-semibold truncate pr-2">{title}</span>
                <div className="flex gap-1 ml-2">
                    {showMinimize && (
                        <button
                            className="accent-hover"
                            onClick={(e) => {
                                e.stopPropagation();
                                onMinimize?.(id);
                            }}
                            title="Minimize"
                        >
                            <FaMinus />
                        </button>
                    )}
                    <button
                        className="hover:text-red-500"
                        onClick={(e) => {
                            e.stopPropagation();
                            onClose(id);
                        }}
                        title="Close"
                    >
                        ×
                    </button>
                </div>
            </div>

            {/* Content */}
            <div 
                className="flex-1 overflow-auto relative" 
                style={{ height: `calc(100% - 46px)` }}
                onMouseDown={handleBodyMouseDown}
                onTouchStart={handleBodyTouchStart}
            >
                {children}
            </div>

            {/* BOTTOM drag handle (transparent strip) */}
            <div
                className="absolute left-0 bottom-0 h-8 cursor-grab active:cursor-grabbing select-none"
                style={{
                    right: showResize ? 40 : 0, // 40px ≈ bottom-right handle area
                }}
                onMouseDown={handleBottomMouseDown}
                onTouchStart={handleBottomTouchStart}
                title="Drag"
            />

            {/* Resize handle */}
            {showResize && (
                <button
                    type="button"
                    className="absolute right-1.5 bottom-1.5 w-5 h-5 rounded-md cursor-nwse-resize z-20 grid place-items-end bg-white/90 border shadow"
                    onMouseDown={onResizeMouseDown}
                    onTouchStart={onResizeTouchStart}
                    aria-label="Resize"
                    title="Resize"
                >
                    <svg width="18" height="18" className="block opacity-70 pointer-events-none">
                        <polyline points="3,15 15,15 15,3" fill="none" stroke="currentColor" strokeWidth="2" />
                    </svg>
                </button>
            )}
        </div>
    );
}
